import { Action } from '@ngrx/store';
import { QMANNING_ENTITY } from 'app/shared/constant';
import { InstallationSeries, InstallationType } from 'app/shared/models/location-details';
import { LocationStatus, RainLocation } from 'app/shared/models/locations';
import { LocationCardMonitorSeries } from 'app/shared/models/monitorsetting';
import { EntityResponseItem } from 'app/shared/models/view-data';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { BaseMonitorSeriesValues, MonitorSeriesNames } from '../../../location-card.constants';
import { LocationCardStatus } from '../../../models/location-card.model';
import {
    attachSectionToRow,
    removeSection,
    enableField,
    loadInstallationTypes,
    pickMapCoords,
    setFieldValueAction,
    setGlobalConfig,
    statusChange,
    tagKeyChange,
} from '../../actions';
import { field } from '../../core/field-schema';
import { asyncCustom, custom, maxLength, pattern, required } from '../../core/validator-builders';
import { Row, RowItemType, Section, SectionType, SelectorIdentifier, UIElementType } from '../../models';
import { ValidatorIdentifier } from '../../models/validator-identifier';
import { BasicInfoCommonFieldIds, InstallationConfigState, InstallationTypeSectionIds } from '../common.models';
import { EdgeDxSectionIds } from './base-config-models';
import { pipeInstallationConfig } from './installation-types/pipe';
import { weirInstallationConfig } from './installation-types/weir';
import { flumeInstallationConfig } from './installation-types/flume';
import { channelInstallationConfig } from './installation-types/channel';
import { elevationInstallationConfig } from './installation-types/elevation';
import { EdgeDxLocationCardState } from './models';

export const customPropertyRowGenerator = (index: number, values?: { key: string; value: string }): Row => {
    return {
        info: { id: `custom-property-${index}`, panelClass: 'custom-50p' },
        items: [
            field.autocomplete<string, string>(`${BasicInfoCommonFieldIds.CustomPropertyKey}-${index}`, 'key', {
                value: values?.key ?? null,
                placeholder: 'ADD_EDIT.INPUT_TAG_NAME',
                panelClass: 'width-90',
                optionsSelector: SelectorIdentifier.TagKeyAutocomplete,
                displayMapper: (option) => option,
                valueMapper: (option) => option,
                validators: [
                    required({ message: 'COMMON.FIELD_REQUIRED_ERROR' }),
                    maxLength({ value: 128, message: 'COMMON.TAG_LENGTH_ERROR' }),
                ],
                events: {
                    onBlur: (value) => [
                        tagKeyChange({
                            fieldId: `${BasicInfoCommonFieldIds.CustomPropertyKey}-${index}`,
                            newValue: value,
                            isMp1: true,
                        }),
                    ],
                },
            }),
            field.text(`${BasicInfoCommonFieldIds.CustomPropertyValue}-${index}`, 'value', {
                value: values?.value ?? null,
                placeholder: 'ADD_EDIT.INPUT_TAG_VALUE',
                panelClass: 'width-90',
                validators: [
                    required({ message: 'COMMON.FIELD_REQUIRED_ERROR' }),
                    maxLength({ value: 128, message: 'COMMON.TAG_LENGTH_ERROR' }),
                ],
            }),
        ],
    };
};

export const basicInfo = (state: EdgeDxLocationCardState): Section => {
    const settings = state.settings;
    if (!settings) {
        throw new Error('Location card settings are not loaded.');
    }

    const details = state.locationDetails[0];
    const isEditMode = !!details;
    const isEdgeDx = details?.series.toLowerCase() === MonitorSeriesNames.edgeDx.toLowerCase();
    const rainGauge = state.options.rainGauges?.data.find(
        (location) => location.locationId === details?.assignedRainGaugeLocationId,
    );
    const showCoordsButton = !!state.locationCardData.enableSelectOnMap;

    const config: Section = {
        info: {
            id: EdgeDxSectionIds.BasicInfo,
            title: 'ADD_EDIT.ADD_EDIT_INFORMATION',
            displayTitle: true,
            expanded: true,
            expandable: true,
            panelClass: EdgeDxSectionIds.BasicInfo,
        },
        rows: [
            {
                info: { id: 'basic-info-row-1', panelClass: 'custom-50p' },
                items: [
                    field.text(BasicInfoCommonFieldIds.LocationName, 'locationName', {
                        value: details?.locationName ?? null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_NAME',
                        validators: [
                            required({ message: 'COMMON.FIELD_REQUIRED_ERROR' }),
                            maxLength({ value: 63 }),
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            custom({
                                identifier: ValidatorIdentifier.UniqueLocationName,
                                message: 'COMMON.LOCATION_NAME_EXISTS',
                            }),
                        ],
                        events: {
                            onChange: (value) => [
                                setFieldValueAction({
                                    fieldId: BasicInfoCommonFieldIds.LocationName,
                                    value: value.trim().charAt(0).toUpperCase() + value.trim().slice(1),
                                }),
                            ],
                        },
                    }),
                    field.select<LocationCardMonitorSeries, string>(BasicInfoCommonFieldIds.Series, 'series', {
                        value: BaseMonitorSeriesValues.EDGE_DX,
                        panelClass: 'custom-width',
                        placeholder: 'COMMON.MONITOR_SERIES_PLACEHOLDER',
                        validators: [required({ message: 'COMMON.FIELD_REQUIRED_ERROR' })],
                        optionsSelector: SelectorIdentifier.MonitorSeriesOptions,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        events: {
                            onChange: (option) => [
                                enableField({ fieldId: BasicInfoCommonFieldIds.InstallationType }),
                                loadInstallationTypes({ installationId: option.monitorSeriesID }),
                                setGlobalConfig({ monitorSeriesName: option.value }),
                            ],
                        },
                    }),
                ],
            },
            {
                info: { id: 'basic-info-row-2', panelClass: 'custom-50p' },
                items: [
                    field.text(BasicInfoCommonFieldIds.SerialNumber, 'serialnumber', {
                        value: details?.serialNumber ?? null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SERIAL',
                        validators: [
                            required({ message: 'COMMON.FIELD_REQUIRED_ERROR' }),
                            maxLength({ value: 20 }),
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWithHyphenOnly_Pattern,
                                message: 'COMMON.INVALID_SERIAL_NUMBER_ERROR',
                            }),
                            asyncCustom({
                                identifier: ValidatorIdentifier.UniqueSerialNumber,
                                message: 'HOME.MAP.MARKER_LOCATION_DETAIL.SERIAL_NUMBER_EXISTS_ERROR',
                            }),
                        ],
                    }),
                    {
                        id: BasicInfoCommonFieldIds.StatusSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: 'status-container',
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'status-group custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'status-row',
                                        panelClass: 'status-inner-row flex align-items-center gap-4',
                                    },
                                    items: [
                                        field.select<LocationCardStatus, number>(
                                            BasicInfoCommonFieldIds.Status,
                                            'status',
                                            {
                                                value: details?.status ?? LocationStatus.Active,
                                                disabled: !isEditMode,
                                                panelClass: 'custom-width',
                                                label: 'Status',
                                                validators: [required({ message: 'COMMON.FIELD_REQUIRED_ERROR' })],
                                                optionsSelector: SelectorIdentifier.LocationStatusesWithNoMaintenance,
                                                valueMapper: (option) => option.key,
                                                displayMapper: (option) => option.name,
                                                events: { onChange: () => [statusChange({ isMp1: true })] },
                                            },
                                        ),
                                    ],
                                },
                            ],
                        },
                    },
                ],
            },
            {
                info: { id: 'basic-info-description-row', panelClass: 'custom-50p' },
                items: [
                    field.text(BasicInfoCommonFieldIds.Description, 'description', {
                        value: details?.description ?? null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LOCATION',
                        validators: [
                            maxLength({ value: 257 }),
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                        ],
                    }),
                ],
            },
            {
                info: { id: 'location-row-1', panelClass: 'custom-50p' },
                items: [
                    field.autocomplete<RainLocation, string>(
                        BasicInfoCommonFieldIds.AssignedRainGauge,
                        'assignedRainGauge',
                        {
                            value: rainGauge?.locationName ?? details?.assignedRainGaugeLocationName ?? null,
                            panelClass: 'custom-width',
                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ASSIGNED',
                            clearable: true,
                            autoClearOnFocus: true,
                            optionsSelector: SelectorIdentifier.RainGaugeAutocomplete,
                            displayMapper: (option) => option.locationName,
                            valueMapper: (option) => option.locationName,
                            validators: [maxLength({ value: 50 })],
                        },
                    ),
                    field.number(BasicInfoCommonFieldIds.ManholeDepth, 'manholedepth', {
                        value: details?.manholeDepth ?? null,
                        panelClass: 'custom-width',
                        placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.MANHOLE_DEPTH',
                        placeholderSuffix: settings.depthUnit,
                        validators: [
                            required(),
                            pattern({
                                value: settings.isMetric
                                    ? REGEX_CONFIG.numeric0_100000With1DecimalPlacesPattern
                                    : REGEX_CONFIG.numeric0_100000With2DecimalPlacesPattern,
                                message: settings.isMetric
                                    ? 'COMMON.NUMERIC_PRECISION_ERROR_METRIC'
                                    : 'COMMON.NUMERIC_PRECISION_ERROR',
                            }),
                            maxLength({ value: 50 }),
                            custom({
                                identifier: ValidatorIdentifier.ManholeDepthToPipeHeightValidator,
                                message: 'COMMON.MAN_HOLE_DEPTH_PIPE_HEIGHT_ERROR',
                            }),
                        ],
                    }),
                ],
            },
            {
                info: { id: 'location-row-2', panelClass: 'custom-50p' },
                items: [
                    field.text(BasicInfoCommonFieldIds.ManholeAddress, 'manholeaddress', {
                        value: details?.manholeAddress ?? null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MANHOLE_ADDRESS',
                        validators: [
                            maxLength({ value: 64 }),
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                        ],
                    }),
                    {
                        id: BasicInfoCommonFieldIds.CoordinatesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: BasicInfoCommonFieldIds.CoordinatesSection,
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'coordinates-group custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'coordinates-row',
                                        panelClass: `coordinates-inner-row ${
                                            showCoordsButton ? 'flex-end' : 'justify-space-between'
                                        }`,
                                    },
                                    items: [
                                        field.number(BasicInfoCommonFieldIds.Latitude, 'latitude', {
                                            value: details?.latitude ?? 0,
                                            panelClass: 'custom-width',
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LATITUDE',
                                            validators: [
                                                pattern({
                                                    value: REGEX_CONFIG.latitudePattern,
                                                    message: 'COMMON.LONGITUDE_ERROR',
                                                }),
                                            ],
                                        }),
                                        field.number(BasicInfoCommonFieldIds.Longitude, 'longitude', {
                                            value: details?.longitude ?? 0,
                                            panelClass: 'custom-width',
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LONGITUDE',
                                            validators: [
                                                pattern({
                                                    value: REGEX_CONFIG.longitudePattern,
                                                    message: 'COMMON.LONGITUDE_ERROR',
                                                }),
                                            ],
                                        }),
                                        {
                                            id: 'move-location-icon',
                                            name: 'moveLocationIcon',
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Image,
                                            visible: showCoordsButton,
                                            imgSrc: '/assets/images/icons/move_icon.png',
                                            imgHeight: 18,
                                            imgWidth: 18,
                                            panelClass: 'move-icon',
                                            events: { onClick: () => [pickMapCoords()] },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                ],
            },
            {
                info: { id: 'installation-date-row', panelClass: 'custom-50p' },
                items: [
                    field.date(BasicInfoCommonFieldIds.InstallationDate, 'installationDate', {
                        value: details?.installationDate ?? null,
                        label: 'ADD_EDIT.INSTALLATION_DATE',
                        dateFormat: `${settings.dateFormat} HH:mm`,
                        datePickerType: 'datetime',
                        timeInterval: 1,
                        panelClass: 'custom-width',
                    }),
                ],
            },
            {
                info: { id: 'installation-type-row', panelClass: 'custom-50p' },
                items: [
                    field.select<InstallationType, string>(
                        BasicInfoCommonFieldIds.InstallationType,
                        'installationType',
                        {
                            value: isEdgeDx ? `${details.installationType}: ${details.installationShapeTypeID}` : '',
                            preserveOldValues: false,
                            placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.INSTALLATION_TYPE',
                            panelClass: 'custom-width',
                            validators: [required({ message: 'COMMON.FIELD_REQUIRED_ERROR' })],
                            optionsSelector: SelectorIdentifier.InstallationTypes,
                            valueMapper: (option) => option.value,
                            displayMapper: (option) => option.text,
                            events: {
                                onChange: (value) => {
                                    const toDispatch: Action[] = [];
                                    const installationSections = [
                                        InstallationTypeSectionIds.Pipe,
                                        InstallationTypeSectionIds.Weir,
                                        InstallationTypeSectionIds.Flume,
                                        InstallationTypeSectionIds.Channel,
                                        InstallationTypeSectionIds.Elevation,
                                    ];

                                    installationSections.forEach((sectionId) => {
                                        toDispatch.push(
                                            removeSection({ sectionId, parentSectionId: EdgeDxSectionIds.BasicInfo }),
                                        );
                                    });
                                    if (value.series === InstallationSeries.Pipe) {
                                        toDispatch.push(
                                            attachSectionToRow({
                                                targetSectionId: EdgeDxSectionIds.BasicInfo,
                                                rowId: 'installation-type-row',
                                                fieldId: InstallationTypeSectionIds.Pipe,
                                                sectionConfig: pipeInstallationConfig(state),
                                            }),
                                        );
                                    } else if (value.series === InstallationSeries.Weir) {
                                        toDispatch.push(
                                            attachSectionToRow({
                                                targetSectionId: EdgeDxSectionIds.BasicInfo,
                                                rowId: 'installation-type-row',
                                                fieldId: InstallationTypeSectionIds.Weir,
                                                sectionConfig: weirInstallationConfig(state),
                                            }),
                                        );
                                    } else if (value.series === InstallationSeries.Flume) {
                                        toDispatch.push(
                                            attachSectionToRow({
                                                targetSectionId: EdgeDxSectionIds.BasicInfo,
                                                rowId: 'installation-type-row',
                                                fieldId: InstallationTypeSectionIds.Flume,
                                                sectionConfig: flumeInstallationConfig(state),
                                            }),
                                        );
                                    } else if (value.series === InstallationSeries.Channel) {
                                        toDispatch.push(
                                            attachSectionToRow({
                                                targetSectionId: EdgeDxSectionIds.BasicInfo,
                                                rowId: 'installation-type-row',
                                                fieldId: InstallationTypeSectionIds.Channel,
                                                sectionConfig: channelInstallationConfig(state),
                                            }),
                                        );
                                    } else if (value.series === InstallationSeries.Elevation) {
                                        toDispatch.push(
                                            attachSectionToRow({
                                                targetSectionId: EdgeDxSectionIds.BasicInfo,
                                                rowId: 'installation-type-row',
                                                fieldId: InstallationTypeSectionIds.Elevation,
                                                sectionConfig: elevationInstallationConfig(state),
                                            }),
                                        );
                                    }

                                    return toDispatch;
                                },
                            },
                        },
                    ),
                    field.select<EntityResponseItem, number>(BasicInfoCommonFieldIds.QFinalEntityID, 'qFinalEntityID', {
                        value: isEdgeDx ? details.qFinalEntityID ?? null : QMANNING_ENTITY,
                        preserveOldValues: false,
                        placeholder: 'COMMON.QFINALTEXT',
                        panelClass: 'custom-width',
                        optionsSelector: SelectorIdentifier.QFinalEntities,
                        valueMapper: (option) => option.entityId,
                        displayMapper: (option) => option.entityName,
                    }),
                ],
            },
            {
                info: { id: 'custom-properties-container-row', panelClass: 'full-width' },
                items: [
                    {
                        id: BasicInfoCommonFieldIds.CustomPropertiesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: BasicInfoCommonFieldIds.CustomPropertiesSection,
                                title: 'ADD_EDIT.TAGS_SECTION_NAME',
                                displayTitle: false,
                                displayActionBtns: true,
                                type: SectionType.Array,
                                expanded: false,
                                expandable: false,
                                itemGenerator: customPropertyRowGenerator,
                                panelClass: 'custom-properties-panel',
                                maxItems: 64,
                            },
                            rows:
                                details?.customProperties?.map((property, index) =>
                                    customPropertyRowGenerator(index, property),
                                ) ?? [],
                        },
                    },
                ],
            },
        ],
    };

    if (!details || !isEdgeDx || details.installationShapeTypeID === undefined) {
        return config;
    }

    const installationConfigGenerator = pickInstallationTypeConfig(details.installationShapeTypeID);
    const sectionId = getInstallationTypeSectionId(details.installationShapeTypeID);
    if (!installationConfigGenerator || !sectionId) {
        return config;
    }

    const installationConfig = installationConfigGenerator(state);
    const installationTypeRowIndex = config.rows.findIndex((row) => row.info.id === 'installation-type-row');
    config.rows.splice(installationTypeRowIndex + 1, 0, {
        info: { id: sectionId, panelClass: 'full-width' },
        items: [{ id: sectionId, type: RowItemType.Section, section: installationConfig }],
    });

    return config;
};

function pickInstallationTypeConfig(
    installationSeries: number,
): ((state: InstallationConfigState) => Section) | undefined {
    if (installationSeries >= InstallationSeries.Pipe && installationSeries < InstallationSeries.Weir) {
        return pipeInstallationConfig;
    } else if (installationSeries >= InstallationSeries.Weir && installationSeries < InstallationSeries.Flume) {
        return weirInstallationConfig;
    } else if (installationSeries >= InstallationSeries.Flume && installationSeries < InstallationSeries.Channel) {
        return flumeInstallationConfig;
    } else if (installationSeries === InstallationSeries.Channel) {
        return channelInstallationConfig;
    } else if (installationSeries === InstallationSeries.Elevation) {
        return elevationInstallationConfig;
    }
    return undefined;
}

function getInstallationTypeSectionId(installationSeries: number): string | undefined {
    if (installationSeries >= InstallationSeries.Pipe && installationSeries < InstallationSeries.Weir) {
        return InstallationTypeSectionIds.Pipe;
    } else if (installationSeries >= InstallationSeries.Weir && installationSeries < InstallationSeries.Flume) {
        return InstallationTypeSectionIds.Weir;
    } else if (installationSeries >= InstallationSeries.Flume && installationSeries < InstallationSeries.Channel) {
        return InstallationTypeSectionIds.Flume;
    } else if (installationSeries === InstallationSeries.Channel) {
        return InstallationTypeSectionIds.Channel;
    } else if (installationSeries === InstallationSeries.Elevation) {
        return InstallationTypeSectionIds.Elevation;
    }
    return undefined;
}
